import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { getToken, setTokens, clearTokens } from '../lib/auth';
import { getCurrentUser, loginWithCredentials, type DirectusUser } from '../lib/directus';

interface AuthState {
  user: DirectusUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: getToken(),
    isAuthenticated: false,
    isLoading: true,
  });

  const loadUser = useCallback(async (token: string) => {
    try {
      const userData = await getCurrentUser(token);
      setState({
        user: userData.data,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      // Token expired or invalid
      clearTokens();
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, []);

  useEffect(() => {
    const token = getToken();
    if (token) {
      void loadUser(token);
    } else {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [loadUser]);

  const login = useCallback(async (email: string, password: string) => {
    const tokens = await loginWithCredentials(email, password);
    setTokens(tokens.access_token, tokens.refresh_token);
    await loadUser(tokens.access_token);
  }, [loadUser]);

  const logout = useCallback(() => {
    clearTokens();
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
